import "./DigitalLogbook.css";
import { useEffect, useState, useContext } from "react";
import {
    collection,
    getDocs,
    setDoc,
    doc,
    serverTimestamp,
    Timestamp,
    query,
    orderBy,
    limit,
    onSnapshot
} from "firebase/firestore";
import {
    PDFDownloadLink,
    Document,
    Page,
    Text,
    View,
    Image,
    StyleSheet,
    usePDF
} from "@react-pdf/renderer";
import { AuthContext } from "../../services/AuthContext";
import EventCard from "../../components/EventCard";
import logo from "../../assets/GoesGreen-300x74.png";
import logoS from "../../assets/GoesGreen-Logo-69x67.png";
import logoARBF from "../../assets/Arrebeef.jpeg";
import { Link, useNavigate } from "react-router-dom";
import { pdf } from '@react-pdf/renderer';

const pdfStyles = StyleSheet.create({
    page: {
        paddingTop: 70,
        paddingLeft: 20,
        paddingRight: 20,
        paddingBottom: 20,
        fontSize: 10
    },
    headerContainer: {
        position: "absolute",
        top: 15,
        left: 20,
        right: 20,
        height: 50
    },

    logo: {
        width: 98,
        height: 40
    },

    title: {
        position: "absolute",
        left: 0,
        right: 0,
        top: 10,
        textAlign: "center",
        fontSize: 18,
        fontWeight: 700
    },
    titleContainer: {
        flex: 1,
        marginLeft: 15
    },
    subtitle: {
        fontSize: 10,
        color: "#666"
    },
    card: {
        border: "1 solid #ddd",
        padding: 8,
        marginBottom: 8
    },
    header: {
        fontSize: 12,
        marginBottom: 5
    },
    text: {
        marginBottom: 3
    }
});

function LogbookPDF({ events }) {
    return (
        <Document>
            <Page size="A4" style={pdfStyles.page}>
                <View style={pdfStyles.headerContainer} fixed>
                    <Image src={logoARBF} style={pdfStyles.logo} />
                    <Text style={pdfStyles.title}>
                        Registro de novedades digital
                    </Text>
                </View>

                {events.map((event) => (
                    <View key={event.firestoreId} style={pdfStyles.card}>
                        <Text style={pdfStyles.header}>{event.event_code}</Text>
                        <Text style={pdfStyles.text}>Operador: {event.operator_id}</Text>
                        <Text style={pdfStyles.text}>Zona: {event.zone}</Text>
                        <Text style={pdfStyles.text}>Empresa: {event.company}</Text>
                        <Text style={pdfStyles.text}>
                            Fecha: {event.start_date_hour?.toDate?.()?.toLocaleString()}
                        </Text>
                        <Text>{event.decription}</Text>
                    </View>
                ))}
            </Page>
        </Document>
    );
}

export default function DigitalLogbook() {
    const { user, firebaseUid, db } = useContext(AuthContext);

    const [loading, setLoading] = useState(true);
    const [allEvents, setAllEvents] = useState([]);
    const [events, setEvents] = useState([]);
    const [showFilters, setShowFilters] = useState(false);
    const [selectedCode, setSelectedCode] = useState("");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");

    const [codes, setCodes] = useState([]);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [eventCodes, setEventCodes] = useState([]);
    const [operators, setOperators] = useState([]);
    const [responsibles, setResponsibles] = useState([]);
    const [showDenied, setShowDenied] = useState(false);
    const [authorizedUids, setAuthorizedUids] = useState([]);
    const [readUids, setReadUids] = useState([]);
    const [loadingAuth, setLoadingAuth] = useState(true);
    const canEdit = firebaseUid && authorizedUids.includes(firebaseUid);
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        event_code: "",
        operator_id: "",
        decription: "",
        company: "",
        start_date_hour: ""
    });

    const [instance, updateInstance] = usePDF({
        document: <LogbookPDF events={events} />
    });

    useEffect(() => {
        if (!db) return;

        const q = query(collection(db, "userRoles"));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const canEdit = [];
            const canRead = [];

            snapshot.forEach((doc) => {
                if (doc.data().canEditPT8 === true) canEdit.push(doc.id);
                if (doc.data().canReadPT8 === true) canRead.push(doc.id);
            });

            setAuthorizedUids(canEdit);
            setReadUids(canRead);
            setLoadingAuth(false);
        });

        return unsubscribe;
    }, [db]);



    useEffect(() => {
        if (loadingAuth) return;
        if (!firebaseUid) return;

        const allowed =
            authorizedUids.includes(firebaseUid) ||
            readUids.includes(firebaseUid);

        if (!allowed) {
            setShowDenied(true);
            setLoading(false);

            const timer = setTimeout(() => {
                navigate("/", { replace: true });
            }, 2000);

            return () => clearTimeout(timer);
        }

        loadEvents();
    }, [
        loadingAuth,
        firebaseUid,
        authorizedUids,
        readUids,
        navigate
    ]);

    const downloadPdf = async () => {

        const blob = await pdf(
            <LogbookPDF events={events} />
        ).toBlob();

        const url = URL.createObjectURL(blob);

        const link = document.createElement('a');

        link.href = url;
        link.download = 'registro_novedades.pdf';

        link.click();

        URL.revokeObjectURL(url);
    };

    const loadEvents = async () => {
        if (!firebaseUid) return;

        try {
            setLoading(true);

            const [
                eventsSnapshot,
                codesSnapshot,
                operatorsSnapshot,
                responsiblesSnapshot
            ] = await Promise.all([
                getDocs(collection(db, "operative_events")),
                getDocs(collection(db, "oe_events_codes")),
                getDocs(collection(db, "oe_operators")),
                getDocs(collection(db, "oe_responsible"))
            ]);

            const data = eventsSnapshot.docs.map((doc) => ({
                firestoreId: doc.id,
                ...doc.data()
            }));

            data.sort((a, b) => {
                const dateA = a.start_date_hour?.toDate?.() || new Date(0);
                const dateB = b.start_date_hour?.toDate?.() || new Date(0);
                return dateB - dateA;
            });

            setAllEvents(data);
            setEvents(data);

            setCodes([...new Set(data.map((x) => x.event_code).filter(Boolean))].sort());
            setEventCodes(codesSnapshot.docs.map((doc) => doc.data().code).sort());
            setOperators(operatorsSnapshot.docs.map((doc) => doc.data().name).sort());
            setResponsibles(responsiblesSnapshot.docs.map((doc) => doc.data().responsible).sort());
        } catch (error) {
            console.error("Error cargando eventos", error);
        } finally {
            setLoading(false);
        }
    };

    const applyFilters = () => {
        let filtered = [...allEvents];

        if (selectedCode) {
            filtered = filtered.filter((item) => item.event_code === selectedCode);
        }

        if (startDate) {
            const start = new Date(startDate);
            filtered = filtered.filter((item) => {
                if (!item.start_date_hour) return false;
                return item.start_date_hour.toDate() >= start;
            });
        }

        if (endDate) {
            const end = new Date(endDate);
            end.setHours(23, 59, 59, 999);
            filtered = filtered.filter((item) => {
                if (!item.start_date_hour) return false;
                return item.start_date_hour.toDate() <= end;
            });
        }

        setEvents(filtered);
        setShowFilters(false);
    };

    const clearFilters = () => {
        setSelectedCode("");
        setStartDate("");
        setEndDate("");
        setEvents(allEvents);
        setShowFilters(false);
    };

    const createEvent = async (e) => {
        e.preventDefault();

        try {
            const eventsRef = collection(db, "operative_events");

            const idQuery = query(eventsRef, orderBy("id", "desc"), limit(1));
            const idSnapshot = await getDocs(idQuery);

            let lastId = 0;
            if (!idSnapshot.empty) {
                lastId = idSnapshot.docs[0].data().id || 0;
            }
            const nextId = Number(lastId) + 1;

            const newDoc = {
                id: nextId,
                event_code: formData.event_code,
                operator_id: formData.operator_id,
                decription: formData.decription,
                company: formData.company,
                start_date_hour: Timestamp.fromDate(new Date(formData.start_date_hour)),
                created_at: serverTimestamp(),
                nemo: "ABEFDI01",
                zone: "ABEFDI01",
                status: true,
                check_by_supervisor: ""
            };

            await setDoc(doc(db, "operative_events", String(nextId)), newDoc);

            setShowCreateModal(false);
            setFormData({
                event_code: "",
                operator_id: "",
                decription: "",
                company: "",
                start_date_hour: ""
            });

            await loadEvents();
        } catch (error) {
            console.error("Error al guardar:", error);
            alert("Error al guardar el registro");
        }
    };

    if (showDenied) {
        return (
            <div className="App">
                <main>
                    <div
                        style={{
                            color: "#cf1919",
                            fontWeight: 600,
                            margin: "2rem auto",
                            textAlign: "center",
                            fontSize: "1.3rem"
                        }}
                    >
                        No puedes acceder a este contenido
                    </div>
                </main>
            </div>
        );
    }

    if (loading) {
        return <div className="loading-dark">Cargando novedades...</div>;
    }

    return (
        <div className="logbook-page-pt8">
            <div className="top-navbar-pt8">
                <Link to="/">
                    <img
                        src={logo}
                        alt="GoesGreen Logo"
                        className="static-logo3 logo-full"
                    />

                    <img
                        src={logoS}
                        alt="GoesGreen Logo"
                        className="static-logo3 logo-small"
                    />
                </Link>

                <div className="nav-left-pt8">
                    <h1>{showCreateModal ? "Nuevo registro" : "Libro de novedades PT8"}</h1>
                </div>

                <div className="nav-right-pt8"></div>
            </div>

            {showCreateModal ? (
                <div className="form-fullscreen-wrapper">
                    <form className="goesgreen-form" onSubmit={createEvent}>
                        <div className="form-item">
                            <label>Código del evento</label>
                            <select
                                value={formData.event_code}
                                onChange={(e) =>
                                    setFormData({ ...formData, event_code: e.target.value })
                                }
                            >
                                <option value="" disabled>Seleccione un código</option>
                                {eventCodes.map((code) => (
                                    <option key={code} value={code}>{code}</option>
                                ))}
                            </select>
                        </div>

                        <div className="form-item">
                            <label>Operador</label>
                            <select
                                value={formData.operator_id}
                                onChange={(e) =>
                                    setFormData({ ...formData, operator_id: e.target.value })
                                }
                            >
                                <option value="" disabled>Seleccione un operador</option>
                                {operators.map((operator) => (
                                    <option key={operator} value={operator}>{operator}</option>
                                ))}
                            </select>
                        </div>

                        <div className="form-item">
                            <label>Descripción</label>
                            <textarea
                                rows={5}
                                placeholder="Descripción del registro"
                                value={formData.decription}
                                onChange={(e) =>
                                    setFormData({ ...formData, decription: e.target.value })
                                }
                            />
                        </div>

                        <div className="form-item">
                            <label>Empresa / Responsable</label>
                            <select
                                value={formData.company}
                                onChange={(e) =>
                                    setFormData({ ...formData, company: e.target.value })
                                }
                            >
                                <option value="" disabled>Seleccione responsable</option>
                                {responsibles.map((item) => (
                                    <option key={item} value={item}>{item}</option>
                                ))}
                            </select>
                        </div>

                        <div className="form-item">
                            <label>Fecha y hora</label>
                            <input
                                type="datetime-local"
                                value={formData.start_date_hour}
                                onChange={(e) =>
                                    setFormData({ ...formData, start_date_hour: e.target.value })
                                }
                            />
                        </div>

                        <div className="form-button-group">
                            <button
                                type="button"
                                className="btn-goesgreen-secondary"
                                onClick={() => setShowCreateModal(false)}
                            >
                                Cancelar
                            </button>
                            <button type="submit" className="btn-goesgreen-primary">
                                Guardar Registro
                            </button>
                        </div>
                    </form>
                </div>
            ) : (
                <>
                    <div className="events-list">
                        {events.map((event) => (
                            <EventCard key={event.firestoreId} event={event} />
                        ))}
                        {events.length === 0 && (
                            <div className="empty-state">No hay registros para mostrar.</div>
                        )}
                    </div>

                    <div className="floating-buttons-container">
                        <button
                            className="pdf-floating-button"
                            onClick={downloadPdf}
                            title="Descargar PDF"
                        >
                            <svg
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="white"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                <polyline points="7 10 12 15 17 10" />
                                <line x1="12" y1="15" x2="12" y2="3" />
                            </svg>
                        </button>

                        <button
                            className="filter-floating-button"
                            onClick={() => setShowFilters(true)}
                        >
                            <svg
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                            >
                                <path d="M3 5h18" />
                                <path d="M6 12h12" />
                                <path d="M10 19h4" />
                            </svg>
                        </button>

                        {canEdit && (
                            <button
                                className="add-floating-button"
                                onClick={() => setShowCreateModal(true)}
                            >
                                +
                            </button>
                        )}
                    </div>

                    {showFilters && (
                        <div
                            className="filter-modal-overlay"
                            onClick={() => setShowFilters(false)}
                        >
                            <div
                                className="goesgreen-filter-modal"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <h3>Filtros</h3>

                                <div className="form-item">
                                    <label>Código</label>
                                    <select
                                        value={selectedCode}
                                        onChange={(e) => setSelectedCode(e.target.value)}
                                    >
                                        <option value="">Sin selección</option>
                                        {codes.map((code) => (
                                            <option key={code} value={code}>
                                                {code}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-item">
                                    <label>Fecha inicio</label>
                                    <input
                                        type="date"
                                        value={startDate}
                                        onChange={(e) => setStartDate(e.target.value)}
                                    />
                                </div>

                                <div className="form-item">
                                    <label>Fecha fin</label>
                                    <input
                                        type="date"
                                        value={endDate}
                                        onChange={(e) => setEndDate(e.target.value)}
                                    />
                                </div>

                                <div className="form-button-group" style={{ marginTop: "24px" }}>
                                    <button
                                        type="button"
                                        className="btn-goesgreen-secondary"
                                        onClick={clearFilters}
                                    >
                                        Limpiar
                                    </button>
                                    <button
                                        type="button"
                                        className="btn-goesgreen-primary"
                                        onClick={applyFilters}
                                    >
                                        Filtrar
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}